import React, { useState, useEffect, useCallback } from 'react';
import { Button, Badge, Input, Modal, Loader, Select, Alert } from './shared';
import Table, { TableHead, TableBody, TableRow, TableCell } from './shared/Table';

// Global token cache to prevent race conditions
let globalVaultToken = null;
let globalTokenExpiry = null;
let tokenRequestPromise = null;

const SecretsManager = ({ service, isOnline }) => {
  const [selectedService, setSelectedService] = useState('shared');
  const [secrets, setSecrets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateSecret, setShowCreateSecret] = useState(false);
  const [showEditSecret, setShowEditSecret] = useState(false);
  const [editingSecret, setEditingSecret] = useState(null);
  const [vaultStats, setVaultStats] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [secretToDelete, setSecretToDelete] = useState(null);
  const [authorizedIPs, setAuthorizedIPs] = useState([]);
  const [showIPSecurity, setShowIPSecurity] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [vaultToken, setVaultToken] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [tokenExpiry, setTokenExpiry] = useState(null);
  
  // New secret form
  const [newSecret, setNewSecret] = useState({
    key: '',
    value: ''
  });

  const services = [
    'shared',
    'whisper-transcription',
    'file-storage',
    'user-service',
    'order-service',
    'notification-service',
    'admin-dashboard',
    'gateway',
    'dns-manager'
  ];

  // Load secrets for selected service
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadSecrets = useCallback(async (serviceName = selectedService) => {
    if (!isOnline) return;

    setLoading(true);
    setError(null);

    try {
      const token = await getVaultToken();
      const response = await fetch(`${service.url}/api/v1/secrets/${serviceName}/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Loaded secrets from API:', data);
        // Transform simple vault response to expected format
        const transformedSecrets = data.map((item) => {
          return {
            secret_key: item.secret_key || item.key, // Support both old and new API
            created_at: item.created_at,
            updated_at: item.updated_at || item.created_at,
            expires_at: item.expires_at,
            created_by: item.created_by || 'system',
            is_expired: item.is_expired || false,
            rotation_enabled: item.rotation_enabled || false,
            is_dummy: false
          };
        });
        console.log('Transformed secrets:', transformedSecrets);
        setSecrets(transformedSecrets);
      } else if (response.status === 401 || response.status === 403) {
        // Token expired or invalid, clear cache and retry once
        globalVaultToken = null;
        globalTokenExpiry = null;
        const newToken = await getVaultToken();
        const retryResponse = await fetch(`${service.url}/api/v1/secrets/${serviceName}/list`, {
          headers: {
            'Authorization': `Bearer ${newToken}`
          }
        });

        if (retryResponse.ok) {
          const data = await retryResponse.json();
          const transformedSecrets = data.map((item) => {
            return {
              secret_key: item.secret_key || item.key,
              created_at: item.created_at,
              updated_at: item.updated_at || item.created_at,
              expires_at: item.expires_at,
              created_by: item.created_by || 'system',
              is_expired: item.is_expired || false,
              rotation_enabled: item.rotation_enabled || false,
              is_dummy: false
            };
          });
          setSecrets(transformedSecrets);
        } else {
          const errorData = await retryResponse.json();
          setError(errorData.detail || 'Failed to load secrets');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to load secrets');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [service.url, selectedService, isOnline]);

  // Load vault statistics - disabled for simple vault
  const loadVaultStats = async () => {
    // Simple vault doesn't have stats endpoint
    setVaultStats(null);
  };

  // Get vault service token with global caching and race condition prevention
  const getVaultToken = async () => {
    // Check if we have a valid cached token globally
    if (globalVaultToken && globalTokenExpiry && new Date() < globalTokenExpiry) {
      console.log('Using cached vault token');
      return globalVaultToken;
    }
    
    // If there's already a token request in progress, wait for it
    if (tokenRequestPromise) {
      console.log('Waiting for existing token request...');
      return await tokenRequestPromise;
    }
    
    // Double-check after the above checks in case another request just completed
    if (globalVaultToken && globalTokenExpiry && new Date() < globalTokenExpiry) {
      console.log('Using cached vault token (double-check)');
      return globalVaultToken;
    }
    
    // Start a new token request
    tokenRequestPromise = (async () => {
      try {
        console.log('Requesting new vault token...');
        const response = await fetch(`${service.url}/api/v1/auth/service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            service_name: 'admin-dashboard'
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          const token = data.access_token;
          const expiry = new Date(Date.now() + (data.expires_in - 60) * 1000); // Expire 1 minute early for safety
          
          // Store globally
          globalVaultToken = token;
          globalTokenExpiry = expiry;
          
          // Also update local state for debugging
          setVaultToken(token);
          setTokenExpiry(expiry);
          
          console.log('Vault token obtained successfully');
          return token;
        } else {
          throw new Error(`Auth failed: ${response.status}`);
        }
      } catch (error) {
        console.error('Failed to get vault token:', error);
        // Clear cached token on error
        globalVaultToken = null;
        globalTokenExpiry = null;
        setVaultToken(null);
        setTokenExpiry(null);
        return null;
      } finally {
        // Clear the promise so subsequent requests can start a new one
        tokenRequestPromise = null;
      }
    })();
    
    return await tokenRequestPromise;
  };

  // Load authorized IPs with geolocation
  const loadAuthorizedIPs = async () => {
    try {
      const token = await getVaultToken();
      if (!token) {
        setError('Failed to authenticate with vault service');
        return;
      }

      const response = await fetch(`${service.url}/api/v1/security/authorized-ips`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuthorizedIPs(data.authorized_ips || []);
      } else {
        console.error('Failed to load authorized IPs:', response.status);
        setAuthorizedIPs([]);
      }
    } catch (error) {
      console.error('Error loading authorized IPs:', error);
      setAuthorizedIPs([]);
    }
  };

  // Create new secret
  const createSecret = async () => {
    if (!newSecret.key || !newSecret.value) {
      setError('Secret key and value are required');
      return;
    }
    
    try {
      const token = await getVaultToken();
      const requestData = {
        secrets: {
          [newSecret.key]: newSecret.value
        }
      };

      const response = await fetch(`${service.url}/api/v1/secrets/${selectedService}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      if (response.ok) {
        setShowCreateSecret(false);
        setNewSecret({ key: '', value: '' });
        loadSecrets();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create secret');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Get secret value for editing
  const getSecretValue = async (secretKey) => {
    try {
      const token = await getVaultToken();
      const response = await fetch(`${service.url}/api/v1/secrets/${selectedService}/${secretKey}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.secret_value;
      } else if (response.status === 401 || response.status === 403) {
        // Token expired or invalid, clear cache and retry once
        globalVaultToken = null;
        globalTokenExpiry = null;
        const newToken = await getVaultToken();
        const retryResponse = await fetch(`${service.url}/api/v1/secrets/${selectedService}/${secretKey}`, {
          headers: {
            'Authorization': `Bearer ${newToken}`
          }
        });

        if (retryResponse.ok) {
          const data = await retryResponse.json();
          return data.secret_value;
        } else {
          const errorData = await retryResponse.json();
          setError(errorData.detail || 'Failed to get secret value');
          return null;
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to get secret value');
        return null;
      }
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  // Start editing a secret
  const startEditSecret = async (secretKey) => {
    console.log('Starting edit for secret:', secretKey);
    if (!secretKey) {
      setError('Invalid secret key');
      return;
    }
    const value = await getSecretValue(secretKey);
    if (value !== null) {
      setEditingSecret({ key: secretKey, value: value, originalKey: secretKey });
      setShowEditSecret(true);
    }
  };

  // Update existing secret
  const updateSecret = async () => {
    if (!editingSecret || !editingSecret.key || !editingSecret.value) {
      setError('Secret key and value are required');
      return;
    }

    try {
      const token = await getVaultToken();
      const keyChanged = editingSecret.originalKey !== editingSecret.key;

      // If key changed, delete the old one first
      if (keyChanged) {
        const deleteResponse = await fetch(`${service.url}/api/v1/secrets/${selectedService}/${editingSecret.originalKey}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!deleteResponse.ok) {
          const errorData = await deleteResponse.json();
          setError(errorData.detail || 'Failed to delete old secret key');
          return;
        }
      }

      // Create/update the secret with the new key
      const requestData = {
        secrets: {
          [editingSecret.key]: editingSecret.value
        }
      };

      const response = await fetch(`${service.url}/api/v1/secrets/${selectedService}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        setShowEditSecret(false);
        setEditingSecret(null);
        // Reload secrets to update the status (dummy vs active)
        loadSecrets();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update secret');

        // If key was changed and new key creation failed, we should restore the old one
        if (keyChanged) {
          const restoreData = {
            secrets: {
              [editingSecret.originalKey]: editingSecret.value
            }
          };
          await fetch(`${service.url}/api/v1/secrets/${selectedService}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(restoreData)
          });
        }
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete secret
  const deleteSecret = async (secretKey) => {
    try {
      const token = await getVaultToken();
      const response = await fetch(`${service.url}/api/v1/secrets/${selectedService}/${secretKey}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setShowDeleteConfirm(false);
        setSecretToDelete(null);
        // Reload secrets to update the list
        loadSecrets();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete secret');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Start delete confirmation
  const startDeleteSecret = (secretKey) => {
    setSecretToDelete(secretKey);
    setShowDeleteConfirm(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Initial load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOnline) {
      loadSecrets();
      loadVaultStats();
      loadAuthorizedIPs();
    }
  }, [isOnline, selectedService, loadSecrets]);

  if (!isOnline) {
    return (
      <Alert variant="error" title="Secrets Vault Offline">
        The secrets vault service is currently unavailable.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Doppler Status Banner */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                <i className="fas fa-cloud text-white text-xl"></i>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Doppler Secrets Management Active
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                All microservices are now using Doppler for secure secrets management with encryption, versioning, and audit logs.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-gray-700">8 Services Connected</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="fas fa-shield-alt text-purple-600"></i>
                  <span className="text-gray-700">Project: digimint-hub</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="fas fa-code-branch text-blue-600"></i>
                  <span className="text-gray-700">Config: dev</span>
                </div>
              </div>
            </div>
          </div>
          <Badge variant="success">ACTIVE</Badge>
        </div>
      </div>

      {/* Legacy Vault Notice */}
      <Alert variant="info" title="Legacy Vault Interface">
        This interface displays the legacy secrets vault. Services are now loading secrets from Doppler.
        The vault remains accessible for reference and emergency fallback purposes.
      </Alert>

      {/* Header with Stats */}
      {vaultStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-600">{vaultStats.total_secrets}</div>
            <div className="text-sm text-gray-700">Total Secrets</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{vaultStats.active_secrets}</div>
            <div className="text-sm text-green-700">Active Secrets</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-600">{vaultStats.expired_secrets}</div>
            <div className="text-sm text-yellow-700">Expired Secrets</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-600">{vaultStats.services_count}</div>
            <div className="text-sm text-purple-700">Services</div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Service Selector */}
          <div>
            <Select
              label="Service"
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              options={services.map(svc => ({ value: svc, label: svc }))}
            />
          </div>
          
          <Button variant="secondary" onClick={() => loadSecrets()} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>

        <div className="flex items-center space-x-2">
          <Button variant="secondary" onClick={() => setShowIPSecurity(true)}>
            <i className="fas fa-lock" style={{ marginRight: '8px', color: '#000000' }}></i>IP Security
          </Button>
          <Button variant="primary" onClick={() => setShowCreateSecret(true)}>
            Create Secret
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="error" title="Error">
          {error}
        </Alert>
      )}

      {/* Secrets List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">
            Secrets for {selectedService} ({secrets.length})
          </h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <Loader size="md" text="Loading secrets..." />
          </div>
        ) : secrets.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 text-4xl mb-4"><i className="fas fa-lock" style={{ fontSize: '48px' }}></i></div>
            <p className="text-gray-500">No secrets found for this service</p>
            <Button variant="primary" onClick={() => setShowCreateSecret(true)} className="mt-4">
              Create First Secret
            </Button>
          </div>
        ) : (
          <Table bordered>
            <TableHead className="bg-gray-50">
              <TableRow>
                <TableCell header>SECRET KEY</TableCell>
                <TableCell header>CREATED</TableCell>
                <TableCell header>STATUS</TableCell>
                <TableCell header>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className="bg-white divide-y divide-gray-200">
              {secrets.map((secret) => (
                <TableRow key={secret.secret_key}>
                  <TableCell>
                    <div className="flex items-center">
                      <i className="fas fa-key" style={{ fontSize: '20px', marginRight: '8px', color: '#000000' }}></i>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{secret.secret_key}</div>
                        <div className="text-xs text-gray-500">by {secret.created_by}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-500">{formatDate(secret.created_at)}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={secret.is_dummy ? 'warning' : 'success'}>
                      {secret.is_dummy ? 'Dummy' : 'Active'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => startEditSecret(secret.secret_key)}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => startDeleteSecret(secret.secret_key)}>
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>


      {/* Create Secret Modal */}
      <Modal
        isOpen={showCreateSecret}
        onClose={() => setShowCreateSecret(false)}
        title="Create New Secret"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowCreateSecret(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={createSecret}>
              Create Secret
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            type="text"
            label="Secret Key"
            placeholder="e.g., database_password"
            value={newSecret.key}
            onChange={(e) => setNewSecret({...newSecret, key: e.target.value})}
            fullWidth
          />

          <Input
            type="textarea"
            label="Secret Value"
            placeholder="Enter secret value..."
            rows={3}
            value={newSecret.value}
            onChange={(e) => setNewSecret({...newSecret, value: e.target.value})}
            fullWidth
          />
        </div>
      </Modal>

      {/* Edit Secret Modal */}
      <Modal
        isOpen={showEditSecret && editingSecret !== null}
        onClose={() => {
          setShowEditSecret(false);
          setEditingSecret(null);
        }}
        title={`Edit Secret: ${editingSecret?.key || ''}`}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => {
              setShowEditSecret(false);
              setEditingSecret(null);
            }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={updateSecret}>
              Update Secret
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            type="text"
            label="Secret Key"
            value={editingSecret?.key || ''}
            onChange={(e) => setEditingSecret({...editingSecret, key: e.target.value})}
            fullWidth
            helperText={editingSecret?.originalKey !== editingSecret?.key ? "Changing the key will create a new secret and delete the old one" : ""}
          />

          <Input
            type="textarea"
            label="Secret Value"
            placeholder="Enter new secret value..."
            rows={3}
            value={editingSecret?.value || ''}
            onChange={(e) => setEditingSecret({...editingSecret, value: e.target.value})}
            fullWidth
          />
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm && secretToDelete !== null}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSecretToDelete(null);
        }}
        title="Delete Secret"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => {
              setShowDeleteConfirm(false);
              setSecretToDelete(null);
            }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => deleteSecret(secretToDelete)}>
              Delete Secret
            </Button>
          </div>
        }
      >
        <div>
          <p className="text-gray-700">
            Are you sure you want to delete the secret <strong>{secretToDelete}</strong>?
          </p>
          <p className="text-sm text-gray-500 mt-2">
            This action cannot be undone.
          </p>
        </div>
      </Modal>

      {/* IP Security Modal */}
      <Modal
        isOpen={showIPSecurity}
        onClose={() => setShowIPSecurity(false)}
        title={<><i className="fas fa-lock" style={{ marginRight: '8px', color: '#000000' }}></i>Authorized IP Addresses</>}
        size="xl"
      >
        <div>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  These IP addresses are authorized to access the secrets vault:
                </p>
                <Button variant="primary" size="sm" onClick={loadAuthorizedIPs}>
                  <i className="fas fa-sync-alt" style={{ marginRight: '8px', color: '#000000' }}></i>Refresh
                </Button>
              </div>

              {authorizedIPs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No authorized IP information available</p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {authorizedIPs.map((ip, index) => (
                    <div key={index} className="border rounded-lg p-4 bg-gray-50">
                      <div className="mb-2">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm bg-gray-200 px-2 py-1 rounded">
                            {ip.ip}
                          </span>
                          <span className={`px-2 py-1 text-sm rounded-full ${
                            ip.ip.includes('127.0.0.1') || ip.ip.includes('::1')
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {ip.ip.includes('127.0.0.1') || ip.ip.includes('::1') ? 'LOCAL' : 'REMOTE'}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-800 mt-1">
                          {ip.label}
                        </p>
                      </div>
                      
                      {ip.city && (
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex items-center">
                            <span className="w-16 text-gray-500">City:</span>
                            <span>{ip.city}, {ip.region}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="w-16 text-gray-500">Country:</span>
                            <span>{ip.country}</span>
                          </div>
                          {ip.timezone && (
                            <div className="flex items-center">
                              <span className="w-16 text-gray-500">Timezone:</span>
                              <span>{ip.timezone}</span>
                            </div>
                          )}
                          {ip.isp && (
                            <div className="flex items-center">
                              <span className="w-16 text-gray-500">ISP:</span>
                              <span className="text-sm">{ip.isp}</span>
                            </div>
                          )}
                          {ip.location && ip.location !== 'Local' && (
                            <div className="flex items-center">
                              <span className="w-16 text-gray-500">Location:</span>
                              <span className="text-sm font-mono">{ip.location}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded">
                <h4 className="font-medium text-gray-800 mb-2">Security Information</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Only these IP addresses can access the secrets vault</li>
                  <li>• All access attempts from other IPs are blocked and logged</li>
                  <li>• Contact your administrator to authorize additional IPs</li>
                </ul>
              </div>
            </div>
      </Modal>
    </div>
  );
};

export default SecretsManager;